import { ChevronDownIcon, ChevronUpIcon, PlayIcon } from "@heroicons/react/24/outline";
import { useEffect, useState } from "react";
import type { Audit } from "@/types";
import { frameDurationSeconds, terminalStatuses } from "./replay/constants";
import InteractionLog from "./replay/InteractionLog";
import InteractionPlayer from "./replay/InteractionPlayer";
import ViewportSwitch from "./replay/ViewportSwitch";

type InteractionReplayProps = {
  audit: Audit;
  autoCollapseWhenReady?: boolean;
};

export default function InteractionReplay({ audit, autoCollapseWhenReady = false }: InteractionReplayProps) {
  const [collapsed, setCollapsed] = useState(false);
  const [selectedInteractionId, setSelectedInteractionId] = useState(audit.interactions[0]?.id);
  const [currentFrame, setCurrentFrame] = useState(0);
  const [playing, setPlaying] = useState(false);

  const selectedInteraction =
    audit.interactions.find((interaction) => interaction.id === selectedInteractionId) || audit.interactions[0];
  const terminalAudit = terminalStatuses.includes(audit.status);
  const evaluationReady =
    audit.usability_evaluation.status !== "pending" && audit.usability_evaluation.status !== "running";
  const canToggleCollapse = terminalAudit && (!autoCollapseWhenReady || evaluationReady);

  useEffect(() => {
    if (!autoCollapseWhenReady || !terminalAudit || !evaluationReady) {
      return;
    }

    setCollapsed(true);
  }, [autoCollapseWhenReady, evaluationReady, terminalAudit]);

  useEffect(() => {
    setCurrentFrame(0);
    setPlaying(false);
  }, [selectedInteraction?.id]);

  useEffect(() => {
    if (selectedInteraction?.status !== "running") {
      return;
    }

    setCurrentFrame(Math.max(selectedInteraction.screenshots.length - 1, 0));
  }, [selectedInteraction?.id, selectedInteraction?.status, selectedInteraction?.screenshots.length]);

  useEffect(() => {
    if (!playing || !selectedInteraction) {
      return;
    }

    const frameCount = selectedInteraction.screenshots.length;

    if (currentFrame >= frameCount - 1) {
      setPlaying(false);
      return;
    }

    const timer = window.setTimeout(() => {
      setCurrentFrame((frame) => Math.min(frame + 1, frameCount - 1));
    }, frameDurationSeconds * 1000);

    return () => window.clearTimeout(timer);
  }, [currentFrame, playing, selectedInteraction]);

  if (audit.interactions.length === 0) {
    return null;
  }

  return (
    <section className="mt-6">
      <button
        type="button"
        className={`mb-6 flex w-full items-center justify-between rounded-lg border border-gray-200 p-4 text-left transition-colors duration-150 ${
          canToggleCollapse ? "cursor-pointer hover:border-gray-300 hover:bg-gray-50" : "cursor-default"
        }`}
        onClick={() => canToggleCollapse && setCollapsed((value) => !value)}
        aria-expanded={!collapsed}
      >
        <div className="flex items-center">
          <div className="mr-2 flex h-6 w-6 items-center justify-center text-primary">
            <PlayIcon className="h-6 w-6" aria-hidden="true" />
          </div>
          <div>
            <h2 className="mb-1 text-2xl font-semibold text-gray-800">Interaction Replay</h2>
            <span className="text-base text-gray-600">View the step-by-step interaction and replay</span>
          </div>
        </div>
        {canToggleCollapse ? (
          <span className="text-gray-600">
            {collapsed ? (
              <ChevronDownIcon className="h-6 w-6" aria-hidden="true" />
            ) : (
              <ChevronUpIcon className="h-6 w-6" aria-hidden="true" />
            )}
          </span>
        ) : null}
      </button>

      {!collapsed && selectedInteraction ? (
        <>
          <ViewportSwitch
            interactions={audit.interactions}
            selectedInteractionId={selectedInteraction.id}
            onSelect={(interaction) => setSelectedInteractionId(interaction.id)}
          />
          <div className="mb-8 flex flex-col gap-3 lg:flex-row">
            <InteractionLog
              interaction={selectedInteraction}
              currentFrame={currentFrame}
              onSelectFrame={(frame) => {
                setCurrentFrame(frame);
                setPlaying(false);
              }}
              startUrl={audit.scenario.start_url}
            />
            <InteractionPlayer
              interaction={selectedInteraction}
              currentFrame={currentFrame}
              playing={playing}
              onCurrentFrameChange={(frame) => {
                setCurrentFrame(frame);
                setPlaying(false);
              }}
              onPlayingChange={setPlaying}
            />
          </div>
        </>
      ) : null}
    </section>
  );
}
